import { z } from "zod";
import type { RoleCode } from "../auth";
import { paginationQuery } from "../pagination";

/**
 * TSR access (0058): what a login may do INSIDE TSR — its UI and the TSR-only
 * endpoints (`/tsr/*`, `/app-settings/tsr`). One `tsr_personnel` row per
 * login; TSR users sign in with the normal `/auth/login`.
 *
 * It never replaces the user-group check: writing results still needs an
 * OPERATION group holding the sport and `competition.results`
 * (`PATCH /meets/:id/results`), whatever the TSR access role says.
 */

/** Ported from WSL's `tsr_technical_personnel.access_role` (user, 2026-10-27). */
export const TSR_ACCESS_ROLES = ["ADMINISTRATOR", "SPORT_ADMINISTRATOR", "OPERATOR", "DISPLAY_CONTROLLER"] as const;
export type TsrAccessRole = (typeof TSR_ACCESS_ROLES)[number];

export const TSR_PERSONNEL_STATUSES = ["ACTIVE", "INACTIVE"] as const;
export type TsrPersonnelStatus = (typeof TSR_PERSONNEL_STATUSES)[number];

/**
 * GMS roles that are TSR ADMINISTRATOR with no row, while ACTIVE as that role
 * (one active role per session — a SUPER_ADMIN acting as OPERATION is read
 * from its row like anyone else).
 */
export const TSR_ADMIN_ROLES = ["SUPER_ADMIN", "ADMIN"] as const satisfies readonly RoleCode[];

/** Every access role but ADMINISTRATOR is pinned to ONE sport (`ck_tsr_personnel_sport`). */
export const tsrAccessRoleNeedsSport = (role: TsrAccessRole): boolean => role !== "ADMINISTRATOR";

// ── Wire ────────────────────────────────────────────────────────────────────

/** `GET /tsr/me`. 403 FORBIDDEN_ROLE for a login with no ACTIVE row (unless an admin role is active). */
export interface TsrMe {
	user: { id: string; username: string; name: string };
	active_role: RoleCode;
	access_role: TsrAccessRole | null;
	/** The sports this login works in TSR. Empty with `all_sports` for an ADMINISTRATOR. */
	sport_ids: string[];
	/** ADMINISTRATOR (incl. SUPER_ADMIN / ADMIN): every sport. */
	all_sports: boolean;
	/** The row's status; `ACTIVE` for an admin role with no row. */
	status: TsrPersonnelStatus;
}

/** One `tsr_personnel` row as `/tsr/personnel` returns it. */
export interface TsrPersonnel {
	tsr_personnel_id: string;
	user: { id: string; username: string; name: string };
	access_role: TsrAccessRole;
	sport: { sport_id: string; code: string; name: string } | null;
	status: TsrPersonnelStatus;
	created_at: string;
	updated_at: string;
}

export const TSR_PERSONNEL_SORT_KEYS = ["created_at", "username"] as const;

export const listTsrPersonnelQuery = paginationQuery.extend({
	search: z.string().trim().min(1).max(100).optional(),
	sport_id: z.string().uuid().optional(),
	access_role: z.enum(TSR_ACCESS_ROLES).optional(),
	status: z.enum(TSR_PERSONNEL_STATUSES).optional(),
});
export type ListTsrPersonnelQuery = z.infer<typeof listTsrPersonnelQuery>;

const sportFor = <T extends { access_role?: TsrAccessRole | undefined; sport_id?: string | null | undefined }>(b: T, ctx: z.RefinementCtx) => {
	if (b.access_role === undefined) return;
	const needs = tsrAccessRoleNeedsSport(b.access_role);
	if (needs && !b.sport_id) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["sport_id"], message: "This access role works in one sport: choose it" });
	if (!needs && b.sport_id) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["sport_id"], message: "An administrator works in every sport: leave the sport empty" });
};

export const createTsrPersonnelBody = z
	.object({
		user_id: z.string().uuid(),
		access_role: z.enum(TSR_ACCESS_ROLES),
		sport_id: z.string().uuid().nullable().optional(),
	})
	.superRefine(sportFor);

/** `PATCH /tsr/personnel/:id`: access role and sport go together (an ADMINISTRATOR has none). */
export const updateTsrPersonnelBody = z
	.object({
		access_role: z.enum(TSR_ACCESS_ROLES).optional(),
		sport_id: z.string().uuid().nullable().optional(),
		status: z.enum(TSR_PERSONNEL_STATUSES).optional(),
	})
	.superRefine((b, ctx) => {
		if (b.access_role === undefined && b.sport_id !== undefined) {
			ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["access_role"], message: "Send the access role with the sport" });
		}
		sportFor(b, ctx);
	})
	.refine((b) => b.access_role !== undefined || b.status !== undefined, "Nothing to change");

// ── Personnel candidates (the login picker) ─────────────────────────────────

/** Shortest search the login picker sends: a trigram index needs something to match on. */
export const TSR_CANDIDATE_SEARCH_MIN = 2;

/**
 * `GET /tsr/personnel/candidates` — ACTIVE logins to give TSR access to, for
 * the people who manage TSR personnel (TSR ADMINISTRATORs, GMS admins). `GET
 * /users` is GMS-admin only, so a TSR administrator needs this narrow read.
 * The search is required (trimmed first).
 */
export const listTsrPersonnelCandidatesQuery = paginationQuery.extend({
	search: z.string().trim().min(TSR_CANDIDATE_SEARCH_MIN).max(100),
});
export type ListTsrPersonnelCandidatesQuery = z.infer<typeof listTsrPersonnelCandidatesQuery>;

/** One login the picker offers. No email, no roles: just enough to choose. */
export interface TsrPersonnelCandidate {
	id: string;
	username: string;
	name: string;
	status: "ACTIVE";
	/** Already has a `tsr_personnel` row (any status): edit that row instead. */
	has_tsr_access: boolean;
}
